/**
 * An emoji click while a label or note editor is focused inserts the
 * character at the caret and leaves the tray where it is (ROUND3 §3): the
 * sheet rules that an emoji is "a Unicode character in a text run". The
 * tray has no handle on the editor, so the insert goes through the
 * platform's own text-input path, which Lexical listens to as a
 * `beforeinput` of type `insertText`; the picker on the contextual bar
 * inserts through the editor directly, as it has the instance.
 */

/** The board editor holding the caret — a label, note, shape or callout
 * inside a canvas node — or null. The chat composer and the tray's own
 * fields are not board editors. */
export function focusedEditableElement(): HTMLElement | null {
  const active = document.activeElement as HTMLElement | null;
  if (!active) return null;
  return active.closest<HTMLElement>(
    '.react-flow__node [contenteditable="true"]',
  );
}

/** Returns true when the character went into a focused editor. */
export function insertEmojiIntoFocusedEditor(char: string): boolean {
  const editable = focusedEditableElement();
  if (!editable) return false;
  try {
    return document.execCommand("insertText", false, char);
  } catch {
    return false;
  }
}

/**
 * On a press outside the editor — the Stickers tile, a tab, a cell — keep
 * a focused board editor focused, or the emoji click has no caret to
 * insert at. A press on a field inside the tray takes focus as it should.
 */
export function keepEditorFocus(e: React.PointerEvent<HTMLElement>) {
  const editable = focusedEditableElement();
  if (!editable || e.currentTarget.contains(editable)) return;
  const target = e.target as HTMLElement | null;
  if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
    return;
  }
  e.preventDefault();
}
